import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const incomingGallery = html.querySelector('media-gallery');
        if (!incomingGallery) return;

        // Import into this document so the preview slide below never leaves it (its WebGL
        // canvas shouldn't be adopted into the parsed response document and back).
        const newMediaGallery = /** @type {Element} */ (document.importNode(incomingGallery, true));
        this.#carryOverPreviewSlide(newMediaGallery);

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * [BL] If this gallery holds the By Lumine Personaliser's 3D preview slide, move that same
   * slide (live React/WebGL preview and all) to the front of the incoming gallery and start
   * the incoming slideshow on it, instead of jumping to the new variant's photo. A variant
   * change (e.g. material) then updates the preview in place.
   *
   * @param {Element} newMediaGallery - The gallery about to replace this one.
   */
  #carryOverPreviewSlide(newMediaGallery) {
    const previewSlide = this.querySelector('slideshow-slide:has([data-bl-preview])');
    if (!previewSlide) return;

    const newSlideshow = newMediaGallery.querySelector('slideshow-component');
    const scroller =
      newSlideshow?.querySelector('[ref="scroller"]') || newSlideshow?.querySelector('slideshow-slide')?.parentElement;
    if (!newSlideshow || !scroller) return;

    scroller.insertBefore(previewSlide, scroller.firstElementChild);
    newSlideshow.setAttribute('initial-slide', '0');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
